import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { BRAND } from "../data/content";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/featured-trails", label: "Featured Trails" },
  { to: "/interpretive-signage", label: "Interpretive Signage" },
  { to: "/services", label: "Services" },
  { to: "/about", label: "About" },
  { to: "/become-a-storyteller", label: "Become a Storyteller" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-brand-cream/85 backdrop-blur-xl border-b border-brand-line/60"
          : "bg-transparent"
      }`}
    >
      <div className="container-px flex items-center justify-between h-16 md:h-20">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 group">
          <img
            src={BRAND.logo}
            alt="Talking Trails"
            className="h-12 md:h-14 w-auto object-contain drop-shadow-sm transition-transform duration-500 group-hover:scale-105"
          />
          <div className="leading-tight hidden sm:block">
            <div className={`font-serif text-lg md:text-xl ${scrolled ? "text-brand-charcoal" : "text-brand-cream"}`}>
              Talking Trails
            </div>
            <div className={`text-[10px] uppercase tracking-[0.28em] ${scrolled ? "text-brand-ochre" : "text-brand-cream/80"}`}>
              Every Place Has a Story
            </div>
          </div>
        </Link>

        <nav className="hidden xl:flex items-center gap-7">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.to.replace(/\//g, "") || "home"}`}
              className={({ isActive }) =>
                `text-[13px] tracking-wide font-medium transition-colors duration-300 ${
                  scrolled
                    ? isActive
                      ? "text-brand-ochre"
                      : "text-brand-charcoal hover:text-brand-ochre"
                    : isActive
                    ? "text-brand-cream"
                    : "text-brand-cream/85 hover:text-brand-cream"
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <Link
          to="/contact"
          data-testid="nav-cta"
          className={`hidden xl:inline-flex items-center gap-2 px-3.5 py-1.5 text-[10px] font-medium tracking-[0.18em] uppercase whitespace-nowrap transition-colors duration-300 ${
            scrolled
              ? "bg-brand-rainforest text-brand-cream hover:bg-brand-rainforestDark"
              : "border border-brand-cream/80 text-brand-cream hover:bg-brand-cream hover:text-brand-charcoal"
          }`}
        >
          Start a Conversation
        </Link>

        <button
          onClick={() => setOpen((o) => !o)}
          data-testid="mobile-menu-toggle"
          className={`xl:hidden p-2 -mr-2 ${scrolled ? "text-brand-charcoal" : "text-brand-cream"}`}
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="xl:hidden bg-brand-cream border-t border-brand-line/60">
          <nav className="container-px py-6 flex flex-col gap-4">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`mobile-nav-${n.to.replace(/\//g, "") || "home"}`}
                className={({ isActive }) =>
                  `text-base font-medium tracking-wide ${
                    isActive ? "text-brand-ochre" : "text-brand-charcoal"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
            <Link
              to="/contact"
              data-testid="mobile-nav-cta"
              className="mt-2 inline-flex items-center justify-center bg-brand-rainforest text-brand-cream px-5 py-3 text-xs uppercase tracking-[0.2em]"
            >
              Start a Conversation
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}
